import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet,TouchableOpacity, ScrollView, KeyboardAvoidingView, Linking } from 'react-native'
import { Icon, Button, Text, Spinner, Radio, RadioGroup, Datepicker, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import MoneyTextInput from '../../components/MoneyTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewSpending, deleteSpending } from '../../global/Firebase/firebase'
import { snapshotExists } from '../../global/Firebase/types'
import HeaderIconButton from '../../components/HeaderIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useTranslation } from 'react-i18next'
import { constantsAtom, localeDateServiceAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { getLocalStoreDate, getDailyStatID } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { parseDecimalAmount } from '../../utils/decimalInput'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

export default function SpendingDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const { detail: passedDetail, itemIndex, sectionIndex, addItemToData, removeItemFromData, detailId, preselectedStore } = route.params
  const [detail, setDetail] = useState(passedDetail)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const postHog = usePostHog()
  const [details, setDetails] = useState(detail ? detail.details : null)
  const [category, setCategory] = useState(detail ? detail.category : null)
  const [storeInfo, setStoreInfo] = useState(detail ? (detail.storeInfo || storesDict?.[detail.store] || storesDict?.[detail.storeId]) : (preselectedStore ? storesDict?.[preselectedStore] : storesDict?.[context.user.store.id]))
  const [source, setSource] = useState(detail ? detail.source : null)
  const [type, setType] = useState(detail ? detail.type : 0) // type: 0 - opex, 1 - unusual opex, 2 - capex
  const [amount, setAmount] = useState(detail ? detail.amount.toString() : null)
  const [date, setDate] = useState(detail ? (detail.date?.toDate?.() ? detail.date?.toDate?.() : detail.date) : new Date())
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const _datePicker = React.createRef()

  useEffect(() => {
    if (userRole?.permissions?.deleteSpending || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          detail != null && <HeaderIconButton
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteSpending(db, detail.ref, detail)
                  removeItemFromData(sectionIndex, itemIndex)
                  setIsLoading(false)
                  navigation.goBack()
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'spending-delete' } })
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
          />
        ),
      })
    }
  }, [isLoading, detail])

  // Log spending detail for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[SPENDING_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        amount: detail.amount,
        category: detail.category,
        details: detail.details,
        type: detail.type,
        store: detail.storeInfo?.id,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [detail])

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        let spendingRef = db.collection('spendings').doc(detailId)
        let doc = await spendingRef.get()
        if (snapshotExists(doc)) {
          let fetchedDetail = doc.data()
          fetchedDetail.ref = spendingRef
          fetchedDetail.id = spendingRef.id
          setDetail(fetchedDetail)
          setDetails(fetchedDetail.details)
          setCategory(fetchedDetail.category)
          setStoreInfo(fetchedDetail.storeInfo)
          setSource(fetchedDetail.source)
          setType(fetchedDetail.type)
          setAmount(fetchedDetail.amount.toString())
          setDate(fetchedDetail.toDate())
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      }
    };

    if (detailId) {
      fetchDetail();
    }
  }, [detailId])

  async function save() {
    if (detail == null) {
      setIsLoading(true)
      // ATL-1371 — `parseFloat` read the locale comma a decimal keypad emits as a truncation:
      // '36,5' became 36 with no NaN for the guard below to catch. `parseDecimalAmount` returns
      // null for anything unreadable, so the existing error is shown instead of a wrong spend.
      // `<= 0`, not `=== 0`: this screen's own message is «Сумма должна быть числом больше 0» and
      // it was not enforcing the sign it promises — a pasted '-500' booked a negative spending.
      const numberAmount = parseDecimalAmount(amount)
      if (numberAmount === null || numberAmount <= 0) {
        setSaveError(t('spendings.notANumberError'))
        setIsLoading(false)
        return
      }
      if (source != null && category != null) {
        setSaveError('')
        let info = {
          details: details,
          category: category,
          type: type,
          amount: numberAmount,
          source: source,
          date: date,
          dailyStatID: getDailyStatID(storeInfo.id, date),
          cashierUID: context.user.uid,
          store: storeInfo.id,
          storeInfo: storeInfo,
          organizationId: context.user.organizationId
        }
        try {
          const { ref, transactionId } = await saveNewSpending(db, info)
          // ATL-1790: the cached item names the leg this save booked — `deleteSpending` removes the
          // leg only through it, so a spending deleted from this copy must not leave its money live.
          addItemToData?.({...info, transactionId, ref, id: ref.id}, info.date, info.store)
          sendFBAnalyticsEvent('new_spending')
          postHog.capture('spending_created')
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'spending-create' } })
          console.log(error)
          setSaveError(saveFailureText(error, t, t('error')))
          setIsLoading(false)
        }
      } else {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
      }
    }
  }

  if (detailId && !detail) {
    return <View style={styles.spinner}><Spinner /></View>
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
        { !detail && context.user.role === 'admin' && 
          <Button style={{alignSelf: 'flex-end', marginRight: -20}} appearance='ghost' size='small' onPress={() => {
            Linking.openURL('https://atlas-app.notion.site/577d6dbf02aa4c4ab51a48b029bf0038')
          }} accessoryLeft={(props) => (<Icon name='question-mark-circle-outline' {...props} style={{...props.style, marginRight: 0}} />)}>{t('spendings.instructions')}</Button>
        }
        { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
          <>
            <Text category='label' style={styles.label}>
              {t('store')}
            </Text>
            <PlatformDependantSelect
              data={Object.values(storesDict)}
              selectedOption={storeInfo}
              disabled={detail != null}
              onSelect={(itemValue, itemIndex) => {
                setStoreInfo(itemValue)
              }}
            />
          </>
        )}
        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label'>
            {t('spendings.category')}
          </Text>
          { (userRole?.permissions?.['editSpendingCategories'] || context.user.role === 'admin') && (
            <TouchableOpacity onPress={() => {
                navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['adminSpendingCategories', 'data'], title: t('spendings.adminSpendingCategories'), ref: constants?.adminSpendingCategories.ref})
              }} 
              style={{marginLeft: 5}}
            >
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )}
        </View>
        <PlatformDependantSelect
          data={(userRole?.permissions?.['viewAdminSpendingCategories'] || context.user.role === 'admin') ? [...(constants?.adminSpendingCategories?.data || []), ...(constants?.spendingCategories?.data || [])] : constants?.spendingCategories?.data}
          selectedOption={category}
          disabled={detail != null}
          onSelect={(itemValue, itemIndex) => {
            setCategory(itemValue)
          }}
        />
        <Text category='label' style={styles.label}>
          {t('spendings.details')}
        </Text>
        <StyledTextInput 
          value={details}
          onChangeText={text => setDetails(text)}
          // style={styles.quantityField}
          returnKeyType='done'
          disabled={detail != null}
          selectTextOnFocus={true}
        />
        <Text category='label' style={styles.label}>
          {t('spendings.amount')}
        </Text>
        <MoneyTextInput
          value={amount}
          onChangeText={text => setAmount(text)}
          placeholder='0'
          // style={styles.quantityField}
          returnKeyType='done'
          disabled={detail != null}
          selectTextOnFocus={true}
        />
        <Text category='label' style={styles.label}>
          {t('spendings.source')}
        </Text>
        <PlatformDependantSelect
          data={constants?.wallets.data}
          selectedOption={source}
          disabled={detail != null}
          onSelect={(itemValue, itemIndex) => {
            setSource(itemValue)
          }}
        />
        { (userRole?.permissions?.['viewAdminSpendingCategories'] || context.user.role === 'admin') && (
          <>
            <Text category='label' style={styles.label}>
              {t('date')}
            </Text>
            <Datepicker
              placement='top'
              date={detail ? getLocalStoreDate(storeInfo?.id, date) : date}
              dateService={localeDateService}
              disabled={detail != null}
              onSelect={(date) => {
                setDate(date)
                _datePicker.current.blur()
              }}
              min={new Date('2020-12-01')}
              max={new Date('2029-12-31')}
              ref={_datePicker}
            />
            <Text category='label' style={styles.label}>
              {t('spendings.type')}
            </Text>
            <RadioGroup
              selectedIndex={type}
              onChange={(index) => {
                setType(index)
              }}
              disabled={detail != null}
            >
              <Radio style={styles.radio} disabled={detail != null}>
                {t('spendings.OPEX')}
              </Radio>
              <Radio style={styles.radio} disabled={detail != null}>
                {t('spendings.CAPEX')}
              </Radio>
            </RadioGroup>
          </>
        )}
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        {successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && <Button onPress={save} style={styles.submitButton} disabled={detail != null}>
            {t('save')}
          </Button>
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  }
});